export enum PulseFlow {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

// Convenience enum for now - eventually these values will move to PulseProtocol and the message type field on 
// indvidual messages will hold business meaning values like 'order', 'inventory, etc.
export enum PulseMessageType {
  AS2 = "as2",
  SFTP = "sftp",
  API = "api",
}

// These are dynamically loaded from the protocols table in the datastore
export interface PulseProtocol {
  id: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface HeartbeatMessagesQueryParams {
  environment: string;
  type?: string;
  accountId?: string | null;
  partnerId?: string | null;
  start?: string | null;
  end?: string | null;
}

export interface HeartbeatMessagesQueryParamsv2 {
  environment: string;
  type: string;
  status?: string | null;
  accountId?: string | null;
  partnerId?: string | null;
  uuid?: string | null;
  fileName?: string | null;
  flow?: PulseFlow | null;
  correlationId?: string | null;
  origin?: string | null;
  destination?: string | null;
  locationKey?: string | null;
  as2MessageId?: string | null;
  datesRange?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface HeartbeatFileResponse {
  type: string;
  data: Buffer;
}

export interface PulseAs2Connection {
  id?: number;
  partner_id?: string;
  partner_url?: string;
  shared?: boolean;
  shared_edi_field?: string | null;
  signed_mdn?: boolean;
  sync_mdn?: boolean;
  compression_type?: string;
  encryption_type?: string;
  sign_scheme?: string;
  sign_type?: string;
  algorithm_id_protection?: boolean;
  active: boolean;
  default_config_id?: number;
  effectivePulseConfigId?: number;
  created_at?: string;
  updated_at?: string;

  accounts?: PulseAS2Account[];
  certificates?: PulseAS2Certificate[];
  ssl_certificates?: PulseAS2SSLCertificate[];
  default_config?: PulseAs2ConnectionConfigRule;
  associatedAccounts?: AssociatedAccount[];
}

export interface PulseConfig {
  id: number;
  as2_connection_id?: number;
  effective_date: string;
  expiration_date: string;
  primary_config?: boolean;
}

export interface PulseAs2ConnectionConfig {
  id?: number;
  connection_id: number;
  partner_url: string;
  signed_mdn: boolean;
  sync_mdn: boolean;
  compression_type: string;
  encryption_type: string;
  sign_scheme: string;
  sign_type: string;
  algorithm_id_protection: boolean;
  active: boolean;
  is_default?: boolean;
  pulse_as2_id?: string;
  created_at?: Date;
  updated_at?: Date;
  // TODO: Do we need these here? I think we want to load them separately?
  certificates?: PulseAS2Certificate[];
  ssl_certificates?: PulseAS2SSLCertificate[];
}

export interface PulseAs2ConnectionConfigRule extends PulseAs2ConnectionConfig {
  customer_id?: number;
  rule_updated_at?: Date;

  priority?: number;
  rules?: Record<string, MetadataRule>;
}

export interface OutboundConfigAccountRules {
  connection_id: number;
  connection_config_id?: number;
  account_id: string;
  rules: Record<string, MetadataRule>;
  protocol: string;
  priority: number;
}

export interface PulseCustomer {
  id?: number;
  name: string;
}

export interface AssociatedAs2Connection {
  id: number;
  partner_id: string;
  active: boolean;
}

export interface AssociatedAccount {
  account_id: string;
}

export interface PulseCustomerAccount {
  account_id: string;
  oldId?: string;
  customer_id?: number;
  customer_name?: string;
  associatedConnections?: AssociatedAs2Connection[];
}

export interface PulseWizardPayload {
  customer?: PulseCustomer;
  account: PulseCustomerAccount;
  connection: PulseAs2Connection;
  connectionConfig?: PulseAs2ConnectionConfig;
  inboundMapping?: InboundAccountMappingDetail;
  outboundMapping?: OutboundAccountMappingDetail;
  delivery: AccountProductDeliveryConfig;
}

export interface PulseAS2Account {
  id?: number;
  as2_connection_id?: number;
  account_id: string;
  sender_id?: string;
  product_delivery_method?: string;
  connection_config_override?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PulseBaseCertificate {
  id?: number;
  as2_connection_id?: number;
  as2_connection_config_id?: number;
  cert_base64: string;
  cert?: string;
  active?: boolean;
  archived?: boolean;
  serial_number?: string;
  effective_date: string;
  expiration_date: string;
  created_at?: string;
  updated_at?: string;
  pulse_as2_id?: string;
}

export interface PulseAS2Certificate extends PulseBaseCertificate {
  alias: string;
}

export interface PulseAS2SSLCertificate extends PulseBaseCertificate {
  signature?: string;
  domain?: string;
}

export interface PulseAS2Error {
  id?: string;
  messageId: string;
  payload: string;
  timestamp: string;
  error: string;
  destination?: any;
  sqs?: string;
  entityType?: string;
}

export interface PulseCorrelationMessage {
  account_id: string;
  job_id: string;
  process_id: string;
  efs_file_path: string;
  cup_message_id: string;
  ttl: number;
}

export interface UpdateAttributes {
  updateExpression: string;
  expressionAttributeNames: { [key: string]: any };
  expressionAttributeValues: { [key: string]: any };
}

export enum PulseTagColors {
  SUCCESS = "#3CB371",
  FAILED = "#F88379",
  IN_PROGRESS = "#21BFEB",
  INBOUND = "#008080",
  OUTBOUND = "#837E7C",
  US_EAST_1 = "#5F9EA0",
  US_WEST_2 = "#E0B0FF",
  ACTIVE = "#3CB372",
  NON_ACTIVE = "#F88378",
  DSCO = "#7f03fc",
  ORDERSTREAM = "#0303fc",
  CHANNELADVISOR = "#1cfc03",
  CUSTOMER = "#fc0b03",
  AS2 = "#fcfc03",
  SFTP = "#03fcfc",
}

export enum InboundSteps {
  INBOUND_REQUEST_VALIDATION = "inbound-request-validation",
  FILE_TRANSFERRED_TO_S3 = "file-transferred-to-s3",
  CREATE_LEDGER_FROM_INBOUND = "create-ledger-from-inbound",
  MDN_SENT_TO_CUSTOMER = "mdn-sent-to-customer",
  MDN_UPLOADED_TO_S3 = "mdn-uploaded-to-s3",
  MPE_RULES_ENGINE_PROCESSING = "mpe-rules-engine-processing",
  // Account-mapping cascade steps (TSF-1747): one per routing-diagram node
  // so the message path can be read directly instead of inferred.
  DIRECT_ACCOUNT_MAPPING = "direct-account-mapping",
  SINGLE_RULE_SHORTCUT = "single-rule-shortcut",
  METADATA_ACCOUNT_MAPPING = "metadata-account-mapping",
  METADATA_RULES_NO_MATCH = "metadata-rules-no-match",
  EDI_PARSER_S3_DOWNLOAD = "edi-parser-s3-file-download",
  EDI_PARSER_ACCOUNT_ID_PARSED = "edi-parser-account-id-parsed",
  ACCOUNT_ID_MAPPED = "account-id-mapped",
  DESTINATION_STREAM_DETERMINED = "destination-stream-determined",
  FILE_COPIED_TO_DELIVERY_S3 = "file-copied-to-delivery-s3",
  INBOUND_PRESIGNED_URL_GENERATED = "presigned-url-generated",
  DELIVERED_TO_DESTINATION_STREAM = "delivered-to-destination-stream",
  PROCESSING_IPWORKS_EDI_INBOUND = "processing-ipworks-edi",
}

export enum OutboundSteps {
  OUTBOUND_PRESIGNED_URL_CREATED = "outbound-presigned-url-created",
  PARTNER_ID_RETRIEVED = "partner-id-retrieved",
  GATEWAY_ROUTING_PERFORMED = "gateway-routing-performed",
  AS2_MESSAGE_PICKED_UP = "as2-message-picked-up",
  AS2_FILE_DOWNLOADED_FROM_S3 = "as2-file-downloaded-from-s3",
  MDN_RECEIVED_FROM_CUSTOMER = "mdn-received-from-customer",
  MDN_ACKNOWLEDGED_SENT = "mdn-acknowledged-sent",
  CREATE_LEDGER_FROM_OUTBOUND = "create-ledger-from-outbound",
  POST_FILE_MDN_UPLOADING = "post-file-mdn-uploading",
  GENERAL_OUTBOUND_REQUEST = "general-outbound-request",
  PROCESSING_IPWORKS_EDI_OUTBOUND = "processing-ipworks-edi-outbound",
}

export enum GenericSteps {
  DLQ_ERROR = "dlq-error",
  S3_FILE_OPERATIONS = "s3-file-operations",
  FILE_PICKED_UP_BY_FMS = "file-picked-up-by-fms",
  FMS_FILE_PROCESSED = "fms-file-processed",
  // Delivery-decision routes (TSF-1747): which side of the internal/outbound
  // split the message took at the "Delivery Decision" node.
  INTERNAL_ROUTE_SELECTED = "internal-route-selected",
  OUTBOUND_ROUTE_SELECTED = "outbound-route-selected",
}

export function PulseStepsMap(step: string): string {
  switch (step) {
    case InboundSteps.INBOUND_REQUEST_VALIDATION:
      return "Inbound request validation";
    case InboundSteps.FILE_TRANSFERRED_TO_S3:
      return "File upload to S3";
    case InboundSteps.CREATE_LEDGER_FROM_INBOUND:
      return "Ledger entry creation for inbound message";
    case InboundSteps.MDN_SENT_TO_CUSTOMER:
      return "Send MDN to customer";
    case InboundSteps.MDN_UPLOADED_TO_S3:
      return "MDN upload to S3";
    case InboundSteps.MPE_RULES_ENGINE_PROCESSING:
      return "Message processed by Rules Engine";
    case InboundSteps.DIRECT_ACCOUNT_MAPPING:
      return "Account resolved by direct mapping";
    case InboundSteps.SINGLE_RULE_SHORTCUT:
      return "Account resolved by single-rule shortcut";
    case InboundSteps.METADATA_ACCOUNT_MAPPING:
      return "Account resolved by metadata rule";
    case InboundSteps.METADATA_RULES_NO_MATCH:
      return "No metadata rule matched";
    case InboundSteps.EDI_PARSER_S3_DOWNLOAD:
      return "File downloaded by EDI Parser";
    case InboundSteps.EDI_PARSER_ACCOUNT_ID_PARSED:
      return "EDI Parser determined account ID";
    case InboundSteps.ACCOUNT_ID_MAPPED:
      return "Internal account ID mapped";
    case InboundSteps.DESTINATION_STREAM_DETERMINED:
      return "Internal destination determined";
    case InboundSteps.FILE_COPIED_TO_DELIVERY_S3:
      return "File prepared for internal delivery";
    case InboundSteps.INBOUND_PRESIGNED_URL_GENERATED:
      return "Internal presigned URL generated";
    case InboundSteps.DELIVERED_TO_DESTINATION_STREAM:
      return "Message delivered to internal system";
    case InboundSteps.PROCESSING_IPWORKS_EDI_INBOUND:
      return "Sending file to URL";
    case OutboundSteps.OUTBOUND_PRESIGNED_URL_CREATED:
      return "Presigned URL generated by internal system";
    case OutboundSteps.PARTNER_ID_RETRIEVED:
      return "External partner ID Determined";
    case OutboundSteps.GATEWAY_ROUTING_PERFORMED:
      return "Message routed to appropriate gateway";
    case OutboundSteps.AS2_MESSAGE_PICKED_UP:
      return "AS2 Message picked up by gateway";
    case OutboundSteps.AS2_FILE_DOWNLOADED_FROM_S3:
      return "Download AS2 message by gateway";
    case OutboundSteps.MDN_RECEIVED_FROM_CUSTOMER:
      return "Receive AS2 MDN from customer";
    case OutboundSteps.MDN_ACKNOWLEDGED_SENT:
      return "Send AS2 MDN ACK to customer";
    case OutboundSteps.CREATE_LEDGER_FROM_OUTBOUND:
      return "Created ledger entry for outbound Message";
    case OutboundSteps.POST_FILE_MDN_UPLOADING:
      return "Uploading MDN request to File Storage";
    case OutboundSteps.PROCESSING_IPWORKS_EDI_OUTBOUND:
      return "Processing outbound request with AS2";
    case OutboundSteps.GENERAL_OUTBOUND_REQUEST:
      return "Processing outbound request";
    case GenericSteps.DLQ_ERROR:
      return "Message sent to DLQ";
    case GenericSteps.S3_FILE_OPERATIONS:
      return "Generic S3 file operations";
    case GenericSteps.FILE_PICKED_UP_BY_FMS:
      return "File picked up for initial processing";
    case GenericSteps.FMS_FILE_PROCESSED:
      return "File completed initial processing";
    case GenericSteps.INTERNAL_ROUTE_SELECTED:
      return "Message routed for internal delivery";
    case GenericSteps.OUTBOUND_ROUTE_SELECTED:
      return "Message routed for outbound delivery";
    default:
      return step;
  }
}

export interface SequesteredOutboundAccountIdsRequest {
  accountIds: string[];
}

export interface AccountMappingDetailSearch {
  accountId?: string;
  connectionId?: string;
  protocol?: string;
  mappingType?: string;
}

export interface AccountMappingDetail {
  connection_id?: number;
  connection_name?: string;
  account_id?: string;
  protocol?: string;
  priority?: number;
  created_at?: Date;
  updated_at?: Date;
}

export interface OutboundAccountMappingDetail extends AccountMappingDetail {
  id?: number;
  // TODO: Idk if we need the connection config here... we can load this separately as well when the dialog opens.
  connection_config?: PulseAs2ConnectionConfig;
  connection_config_id?: number;
  connection_config_name?: string;
  rules: Record<string, MetadataRule>;
  condition?: Record<string, any>;
  metadata_transformations?: Record<string, any>;
  sequester?: boolean;
}

export interface InboundAccountMappingDetail extends AccountMappingDetail {
  id?: number;
  account_mapping_rule_type?: string;
  connection_config_id?: number;
  connection_config_name?: string;
  rules?: Record<string, any>;
}

export interface InternalAccountMappingDetail extends AccountMappingDetail {
  id?: number;
  rules?: Record<string, any>;
  created_at?: Date;
  updated_at?: Date;
}

export interface PulseInboundAccountMappingRuleType {
  id: string;
  type_description: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface PulseRuleBuilderField {
  id: string;
  field_description?: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface PulseFileType {
  id: string;
  name: string;
  description?: string;
}

export interface AccountProductDeliveryConfig {
  id?: number;
  account_id: string;
  priority: number;
  condition?: Record<string, any>;
  metadata_transformations?: Record<string, any>;
  sequestration: boolean;
  product_delivery_method?: ProductDeliveryMethod;
  created_at?: Date;
  updated_at?: Date;
}

export interface ProductDeliveryMethod {
  id: number;
  name?: string;
  delivery_method?: string;
  product_id?: string;
  delivery_method_config_id?: number;
  created_at?: Date;
  updated_at?: Date;
}

export interface PartnerSearchResponse {
  totalCount: number;
  data: PulseAs2Connection[];
}

export interface CustomerSearchResponse {
  totalCount: number;
  customers: PulseCustomer[];
}

export interface CustomerAccountSearchResponse {
  totalCount: number;
  accounts: PulseCustomerAccount[];
}

export interface As2ConfigOptionsResponse {
  sharedEdiFields: string[];
  compressionTypes: string[];
  encryptionTypes: string[];
  signSchemes: string[];
  signTypes: string[];
  restrictedPartnerIds: string[];
}

export interface FileRouteTypeOptionsResponse {
  options: FileRouteType[];
}

export interface FileRouteType {
  fileRouteTypeGUID: string;
  fileTypeId: number;
  description: string;
  author: string;
  directoryName: string;
  validForAS2: boolean;
  validForSFTP: boolean;
  validForPartnerSFTP: boolean;
  direction: string;
  application: string;
  applicationCode: string;
  defaultMd5: boolean;
  defaultRegex: string;
  defaultExtension: string;
  createDate?: string;
  updateDate?: string;
}

export interface HeartbeatMetadataDTO {
  uuid: string;
  sequenceNumber: bigint;
  as2MessageId?: string;
  partnerId?: string;
  accountId?: string;
  flow?: string;
  origin?: string;
  fileName?: string;
  fileType?: string;
  messageType?: string;
  fileSizeBytes?: bigint;
  locationBucket?: string;
  locationKey?: string;
  mdnLocationBucket?: string;
  mdnLocationKey?: string;
  source?: string;
  destination?: string;
  receivedTime?: Date;
  presignedUrl?: string;
  presignedUrlExpires?: number;
  correlationId?: string;
  status?: string;
  messageStatus?: string;
}

export interface HeartbeatEventDTO {
  id: string;
  stepId: string;
  receivedTime: Date;
  region: string;
  status: string;
  message?: string;
  payload: any;
  heartbeatMetadataId: string;
  metadata?: Record<string, any>;
}

export interface HeartbeatMessageDTO {
  messageMetadata: HeartbeatMetadataDTO;
  journey: HeartbeatEventDTO[];
}

export interface Metadata {
  [key: string]: string | undefined;
}

export interface HeartbeatPaginatedLogsQueryResponse {
  results: HeartbeatMessageDTO[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
  nextSearchAfter?: (string | number)[];
}

export interface PulseResendStatus {
  key: string;
  status: "Triggered" | "Error";
  message: string;
}

export interface PulseResendStatusList {
  messages: PulseResendStatus[];
}

export interface PulseTestMessageSend {
  messageType: string;
  flow: PulseFlow;
  accountId: string;
  verify: boolean;
}

export interface MetadataRule {
  //We want this to be as much resilient as possible
  type: MetadataRuleType | string;
  value: string;
}

export enum MetadataRuleType {
  FIXED = 'fixed',
  REGEX = 'regex',
}

export interface PulseConnectionConfiguration {
  connectionConfig: PulseAs2ConnectionConfigRule,
  rulesConfig: OutboundConfigAccountRules,
}

// Connections //

export interface ConnectionSearchResponse {
  totalCount: number;
  data: PulseAs2Connection[]; // expand to include SFTP etc
}

// AS2 Connections //

export interface As2ConnectionDetailSearch {
  connectionId?: string;
  partnerId?: string;
  active?: string;
}

// AS2 Connection Configs //

export interface As2ConnectionConfigSearchResponse {
  totalCount: number;
  data: PulseAs2ConnectionConfig[];
}

export interface As2ConnectionConfigDetailSearch {
  configId?: string;
  connectionId?: string;
  isDefault?: string;
  active?: string;
}

// AS2 Certificates //

export interface As2CertificateDetailSearch {
  certificateId?: string;
  connectionId?: string;
  configId?: string;
  active?: string;
  archived?: string;
}

// Cross-environment copy //

export type CopyOutcome = 'copied' | 'skipped' | 'error';

export interface As2ConnectionCopyRequest {
  sourceEnvironment: string;
  targetEnvironment: string;
  connection: PulseAs2Connection;      // full connection record from source
  config: PulseAs2ConnectionConfig;    // chosen config record from source
}

export interface As2ConnectionCopyResult {
  connectionOutcome: CopyOutcome;
  configOutcome: CopyOutcome;
  alreadyExisted: boolean;   // true when both were skipped (warning case)
  error?: string;
}

export interface AccountCopyRequest {
  sourceEnvironment: string;
  targetEnvironment: string;
  account: PulseCustomerAccount;       // full account record from source
}

export interface AccountCopyResult {
  outcome: CopyOutcome;
  alreadyExisted: boolean;   // true when account was skipped (warning case)
  error?: string;
}
